import type { Contact } from '@/types/email';
import { useEmailStore } from './emailStore';
import { useSearchStore } from './searchStore';
import { useSenderGroupingStore } from './senderGroupingStore';
import { useContactStore } from './contactStore';

const applyContactState = <T extends { contactId?: string | null }>(
  entity: T,
  contactId: string,
  approvedState: Contact['approvedState']
): T | null => {
  if (!entity || entity.contactId !== contactId) {
    return null;
  }
  const approvedSender = approvedState === 'approved';
  return {
    ...entity,
    contactApprovalState: approvedState,
    approvedSender,
  };
};

export function syncContactStateAcrossStores(
  contactId: string,
  approvedState: Contact['approvedState'],
  options: { notificationsEnabled?: boolean } = {}
) {
  if (!contactId) {
    return;
  }

  const { notificationsEnabled } = options;

  const emailStore = useEmailStore.getState();
  // The reader is its OWN surface: the open message (selectedEmail) and the
  // members of an open thread are not necessarily rows in `emails` — a thread
  // list carries one representative per thread, and a deep-linked/search-opened
  // message may not be in the list at all. Scanning only `emails` meant blocking
  // or approving a sender from the detail view changed nothing on screen: the
  // approval banner and the "Block sender" menu item both read
  // email.contactApprovalState off the message the reader is rendering.
  // Collect ids from all three surfaces, then let updateEmailInPlace (the single
  // writer that patches list + index + cache + selectedEmail + selectedThread)
  // apply them.
  const matchingIds = new Set<string>();
  emailStore.emails.forEach((email) => {
    if (email.contactId === contactId) {
      matchingIds.add(email.id);
    }
  });
  if (emailStore.selectedEmail?.contactId === contactId) {
    matchingIds.add(emailStore.selectedEmail.id);
  }
  emailStore.selectedThread?.messages.forEach((message) => {
    if (message.contactId === contactId) {
      matchingIds.add(message.id);
    }
  });
  if (matchingIds.size) {
    const approvedSender = approvedState === 'approved';
    matchingIds.forEach((emailId) => {
      emailStore.updateEmailInPlace(emailId, {
        contactApprovalState: approvedState,
        approvedSender,
      });
    });
  }

  useSearchStore.setState((state) => {
    let changed = false;
    const results = state.results.map((result) => {
      if (result.kind === 'email') {
        const updated = applyContactState(result.email, contactId, approvedState);
        if (updated) {
          changed = true;
          return { ...result, email: updated };
        }
        return result;
      }

      if (result.kind === 'thread') {
        let threadChanged = false;
        const messages = result.thread.messages.map((message) => {
          const updated = applyContactState(message, contactId, approvedState);
          if (updated) {
            threadChanged = true;
            return updated;
          }
          return message;
        });
        if (threadChanged) {
          changed = true;
          return {
            ...result,
            thread: {
              ...result.thread,
              messages,
            },
          };
        }
        return result;
      }

      if (result.kind === 'contact' && result.contact.id === contactId) {
        changed = true;
        return {
          ...result,
          contact: {
            ...result.contact,
            approvedState: approvedState,
            ...(notificationsEnabled === undefined
              ? {}
              : { notificationsEnabled }),
          },
        };
      }

      return result;
    });

    if (!changed) {
      return {};
    }

    return { results };
  });

  useSenderGroupingStore.setState((state) => {
    if (!state.senderDigest) {
      return {};
    }

    let digestEmailsChanged = false;
    const digestEmails = state.senderDigest.emails.map((email) => {
      const updated = applyContactState(email, contactId, approvedState);
      if (updated) {
        digestEmailsChanged = true;
        return updated;
      }
      return email;
    });

    let digestGroupEmailsChanged = false;
    const digestGroupEmails = state.senderDigest.group.emails.map((email) => {
      const updated = applyContactState(email, contactId, approvedState);
      if (updated) {
        digestGroupEmailsChanged = true;
        return updated;
      }
      return email;
    });

    const shouldUpdateGroupMeta =
      state.senderDigest.group.contactId === contactId || digestGroupEmailsChanged;

    if (!digestEmailsChanged && !digestGroupEmailsChanged && !shouldUpdateGroupMeta) {
      return {};
    }

    const nextGroup = shouldUpdateGroupMeta
      ? {
          ...state.senderDigest.group,
          contactApprovalState: approvedState,
          emails: digestGroupEmailsChanged
            ? digestGroupEmails
            : state.senderDigest.group.emails,
        }
      : state.senderDigest.group;

    return {
      senderDigest: {
        ...state.senderDigest,
        group: nextGroup,
        emails: digestEmailsChanged ? digestEmails : state.senderDigest.emails,
      },
    };
  });
}

/**
 * Sender alias merge (contact.merged): the contacts in mergedContactIds no
 * longer exist — their mail now belongs to contactId. Re-key every loaded
 * message through the single in-place writer (list, index, cache, reader,
 * open thread) so sender grouping, which keys on contactId, collapses them
 * immediately, and drop the deleted contacts from the contact lists (pending
 * cards included). The server's per-message email.updated events that follow
 * carry the same contactId plus each message's final mailbox.
 */
export function remapMergedContacts(
  mergedContactIds: string[],
  contactId: string,
  approvedState: Contact['approvedState']
) {
  if (!contactId || !mergedContactIds.length) {
    return;
  }
  const merged = new Set(mergedContactIds);
  const emailStore = useEmailStore.getState();
  const matchingIds = new Set<string>();
  emailStore.emails.forEach((email) => {
    if (email.contactId && merged.has(email.contactId)) {
      matchingIds.add(email.id);
    }
  });
  if (emailStore.selectedEmail?.contactId && merged.has(emailStore.selectedEmail.contactId)) {
    matchingIds.add(emailStore.selectedEmail.id);
  }
  emailStore.selectedThread?.messages.forEach((message) => {
    if (message.contactId && merged.has(message.contactId)) {
      matchingIds.add(message.id);
    }
  });
  const approvedSender = approvedState === 'approved';
  matchingIds.forEach((emailId) => {
    emailStore.updateEmailInPlace(emailId, {
      contactId,
      contactApprovalState: approvedState,
      approvedSender,
    });
  });

  useContactStore.getState().removeContacts(mergedContactIds);
}
